/**
 * @module Validation
 * @description Módulo de validação de documentos brasileiros (CNPJ).
 * Fornece funções para validar documentos oficiais de acordo com os algoritmos da Receita Federal.
 * Suporta o novo formato alfanumérico de CNPJ introduzido em 2026.
 */

/**
 * Valida CNPJ no formato alfanumérico da Receita Federal (novo padrão de 2026).
 * Aceita os 12 primeiros caracteres alfanuméricos (A-Z e 0-9) e 2 dígitos verificadores numéricos.
 * Remove automaticamente caracteres de máscara (pontos, barras e hífens).
 *
 * @function validarCNPJ
 * @param {string} cnpj - CNPJ a ser validado (com ou sem máscara). Formato: XX.XXX.XXX/XXXX-XX
 * @returns {boolean} true se o CNPJ é válido, false caso contrário
 *
 * @example
 * // Exemplo 1: Validação básica de CNPJ com máscara
 * import { validarCNPJ } from '@welm';
 *
 * const cnpj = '12.ABC.345/0001-67';
 * const valido = validarCNPJ(cnpj);
 *
 * if (valido) {
 *   console.log('CNPJ válido');
 * } else {
 *   console.log('CNPJ inválido');
 * }
 *
 * @example
 * // Exemplo 2: Validação em formulário React com feedback
 * import { validarCNPJ } from '@welm';
 * import { useNotify } from '@welm';
 * import { useState } from 'react';
 *
 * function FormularioEmpresa() {
 *   const [cnpj, setCnpj] = useState('');
 *   const [erro, setErro] = useState('');
 *   const notify = useNotify();
 *
 *   const handleSubmit = (e) => {
 *     e.preventDefault();
 *
 *     if (!validarCNPJ(cnpj)) {
 *       setErro('CNPJ inválido');
 *       notify.error('Por favor, informe um CNPJ válido');
 *       return;
 *     }
 *
 *     setErro('');
 *     notify.success('CNPJ válido!');
 *     // Enviar formulário...
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <input
 *         type="text"
 *         value={cnpj}
 *         onChange={(e) => setCnpj(e.target.value)}
 *         placeholder="00.000.000/0000-00"
 *       />
 *       {erro && <span className="erro">{erro}</span>}
 *       <button type="submit">Salvar</button>
 *     </form>
 *   );
 * }
 *
 * @example
 * // Exemplo 3: Validação em tempo real com indicador visual
 * import { validarCNPJ } from '@welm';
 * import { useState } from 'react';
 *
 * function InputCNPJ() {
 *   const [cnpj, setCnpj] = useState('');
 *   const [status, setStatus] = useState(''); // 'valido', 'invalido', ''
 *
 *   const handleChange = (e) => {
 *     const valor = e.target.value;
 *     setCnpj(valor);
 *
 *     // Só valida se tiver pelo menos 14 caracteres
 *     if (valor.replace(/\D/g, '').length >= 14) {
 *       setStatus(validarCNPJ(valor) ? 'valido' : 'invalido');
 *     } else {
 *       setStatus('');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <input
 *         type="text"
 *         value={cnpj}
 *         onChange={handleChange}
 *         className={status}
 *         placeholder="Digite o CNPJ"
 *       />
 *       {status === 'valido' && <span style={{color: 'green'}}>✓ Válido</span>}
 *       {status === 'invalido' && <span style={{color: 'red'}}>✗ Inválido</span>}
 *     </div>
 *   );
 * }
 *
 * @example
 * // Exemplo 4: Validação de lista de CNPJs
 * import { validarCNPJ } from '@welm';
 *
 * function validarListaCNPJs(cnpjs) {
 *   const resultados = cnpjs.map(cnpj => ({
 *     cnpj: cnpj,
 *     valido: validarCNPJ(cnpj)
 *   }));
 *
 *   const validos = resultados.filter(r => r.valido);
 *   const invalidos = resultados.filter(r => !r.valido);
 *
 *   console.log(`Total: ${cnpjs.length}`);
 *   console.log(`Válidos: ${validos.length}`);
 *   console.log(`Inválidos: ${invalidos.length}`);
 *
 *   return resultados;
 * }
 *
 * // Uso
 * const cnpjs = [
 *   '12.ABC.345/0001-67',
 *   '00.000.000/0000-00',
 *   'AB.CDE.123/0001-45'
 * ];
 * validarListaCNPJs(cnpjs);
 *
 * @example
 * // Exemplo 5: Validação com máscara automática
 * import { validarCNPJ } from '@welm';
 * import { useState } from 'react';
 *
 * function InputCNPJComMascara() {
 *   const [cnpj, setCnpj] = useState('');
 *
 *   const aplicarMascara = (valor) => {
 *     // Remove tudo que não é letra ou número
 *     let limpo = valor.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
 *
 *     // Limita a 14 caracteres
 *     limpo = limpo.substring(0, 14);
 *
 *     // Aplica máscara: XX.XXX.XXX/XXXX-XX
 *     let mascarado = limpo;
 *     if (limpo.length > 2) mascarado = limpo.substring(0, 2) + '.' + limpo.substring(2);
 *     if (limpo.length > 5) mascarado = mascarado.substring(0, 6) + '.' + mascarado.substring(6);
 *     if (limpo.length > 8) mascarado = mascarado.substring(0, 10) + '/' + mascarado.substring(10);
 *     if (limpo.length > 12) mascarado = mascarado.substring(0, 15) + '-' + mascarado.substring(15);
 *
 *     return mascarado;
 *   };
 *
 *   const handleChange = (e) => {
 *     const mascarado = aplicarMascara(e.target.value);
 *     setCnpj(mascarado);
 *   };
 *
 *   const handleSubmit = () => {
 *     if (validarCNPJ(cnpj)) {
 *       alert('CNPJ válido!');
 *     } else {
 *       alert('CNPJ inválido!');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <input
 *         type="text"
 *         value={cnpj}
 *         onChange={handleChange}
 *         placeholder="00.000.000/0000-00"
 *       />
 *       <button onClick={handleSubmit}>Validar</button>
 *     </div>
 *   );
 * }
 *
 * @example
 * // Exemplo 6: Validação antes de enviar para API
 * import { validarCNPJ } from '@welm';
 * import { APIPost, useNotify } from '@welm';
 *
 * async function cadastrarEmpresa(dados) {
 *   const notify = useNotify();
 *
 *   // Validação de CNPJ antes de enviar
 *   if (!validarCNPJ(dados.cnpj)) {
 *     notify.error('CNPJ inválido. Verifique e tente novamente.');
 *     return { success: false, error: 'CNPJ inválido' };
 *   }
 *
 *   notify.loading(true, 'Cadastrando empresa...');
 *
 *   const resultado = await APIPost('/empresas', dados);
 *
 *   notify.loading(false);
 *
 *   if (resultado.success) {
 *     notify.success('Empresa cadastrada com sucesso!');
 *   } else {
 *     notify.error(resultado.message || 'Erro ao cadastrar empresa');
 *   }
 *
 *   return resultado;
 * }
 *
 * @example
 * // Exemplo 7: Validação com múltiplos formatos aceitos
 * import { validarCNPJ } from '@welm';
 *
 * function processarCNPJ(cnpj) {
 *   // Aceita diferentes formatos:
 *   // - Com máscara: 12.ABC.345/0001-67
 *   // - Sem máscara: 12ABC3450001-67
 *   // - Apenas números: 12345678000167 (formato antigo)
 *   // - Formato misto: 12ABC345000167
 *
 *   const valido = validarCNPJ(cnpj);
 *
 *   if (!valido) {
 *     return {
 *       valido: false,
 *       mensagem: 'CNPJ inválido. Verifique os dígitos verificadores.'
 *     };
 *   }
 *
 *   // Remove máscara para armazenar no banco
 *   const cnpjLimpo = cnpj.replace(/[./-]/g, '').toUpperCase();
 *
 *   return {
 *     valido: true,
 *     cnpjOriginal: cnpj,
 *     cnpjLimpo: cnpjLimpo,
 *     mensagem: 'CNPJ válido'
 *   };
 * }
 *
 * @remarks
 * - **Novo formato alfanumérico**: A partir de 2026, a Receita Federal permite CNPJs com letras
 *   (A-Z) nos primeiros 12 caracteres, mantendo apenas os 2 últimos dígitos como numéricos (DVs).
 * - **Remove máscara automaticamente**: A função aceita CNPJ com ou sem pontos, barras e hífens.
 * - **Case insensitive**: Letras maiúsculas e minúsculas são aceitas (convertidas para maiúsculas internamente).
 * - **Algoritmo de validação**: Usa o algoritmo oficial da Receita Federal com pesos [6,5,4,3,2,9,8,7,6,5,4,3,2]
 *   e módulo 11 para cálculo dos dígitos verificadores.
 * - **Rejeita CNPJs inválidos conhecidos**: CNPJ "00000000000000" é automaticamente rejeitado.
 * - **Formato esperado**: 12 caracteres alfanuméricos + 2 dígitos verificadores numéricos (total: 14 caracteres).
 * - **Caracteres ASCII**: A validação usa códigos ASCII (charCodeAt) para suportar letras e números.
 * - **Performance**: Validação rápida, adequada para uso em tempo real (onChange em inputs).
 * - **Compatibilidade**: Suporta tanto CNPJs numéricos antigos quanto o novo formato alfanumérico.
 *
 * @see {@link module:useNotify|useNotify} - Hook para exibir notificações de validação
 * @see {@link module:APIPost|APIPost} - Método para enviar dados validados para API
 */
export const validarCNPJ = (cnpj) => {
  // Remove máscara (pontos, barras e hífens)
  const cnpjLimpo = cnpj.replace(/[./-]/g, '').toUpperCase();

  // Verifica se tem 14 caracteres e apenas caracteres válidos (A-Z e 0-9)
  if (!/^([A-Z\d]){12}(\d){2}$/i.test(cnpjLimpo) || cnpjLimpo === '00000000000000') {
    return false;
  }

  // Calcula os dígitos verificadores
  const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let soma1 = 0;
  let soma2 = 0;

  // Calcula soma para o primeiro DV
  for (let i = 0; i < 12; i++) {
    // Converte o caractere para seu valor ASCII e subtrai 48
    const valor = cnpjLimpo.charCodeAt(i) - 48;
    soma1 += valor * pesos[i + 1];
    soma2 += valor * pesos[i];
  }

  // Calcula primeiro dígito verificador
  const dv1 = soma1 % 11 < 2 ? 0 : 11 - (soma1 % 11);

  // Adiciona o primeiro DV na soma do segundo DV
  soma2 += dv1 * pesos[12];

  // Calcula segundo dígito verificador
  const dv2 = soma2 % 11 < 2 ? 0 : 11 - (soma2 % 11);

  // Compara os dígitos calculados com os informados
  return cnpjLimpo.substring(12) === `${dv1}${dv2}`;
};
